import { createEffect, createSignal, For, Match, on, Show, Switch } from "solid-js";
import InlineMessage from "../components/inlineMessage/InlineMessage";
import { get, isEmpty } from "lodash-es";
import { TextAndIconButton, TextButton } from "../components/button/Button";
import type { IntegrationVerificationResultDto } from "../services/ApiService";
import pMinDelay from "p-min-delay";
import { MINIMUM_ACTION_LOADING_DELAY } from "../constants";
import logger from "../utils/logger";
import { trackDeep } from "@solid-primitives/deep";
import type { IncompleteIntegrationDefinition, Integration } from "./types";
import { useFormServices } from "../services/formServices";
import { useTranslation } from "../state/i18n";
import { condition, narrow } from "../utils/solidUtils";

type IntegrationVerificationProps<T, DTO> = {
  integration: Integration<T>;
  integrationType: IncompleteIntegrationDefinition<T, DTO>;
  hasErrors: boolean;
};

export const IntegrationVerification = <T, DTO>(props: IntegrationVerificationProps<T, DTO>) => {
  const { t } = useTranslation();
  const { integrationService } = useFormServices();
  const [verificationResult, setVerificationResult] = createSignal<IntegrationVerificationResultDto<T>>();
  const [verifying, setVerifying] = createSignal(false);

  const verify = async () => {
    setVerifying(true);
    try {
      const result = await pMinDelay(
        integrationService.verifyIntegration(props.integration),
        MINIMUM_ACTION_LOADING_DELAY,
        { delayRejection: false },
      );
      setVerificationResult(result);
    } catch (e: unknown) {
      logger.error("Failed to verify integration", e);
      setVerificationResult({
        status: "NOT_VALIDATED",
      });
    } finally {
      setVerifying(false);
    }
  };

  // Allow to verify again when changes have been made after previous successful verification result
  createEffect(
    on(
      () => trackDeep(props.integration),
      () => {
        if (verificationResult()?.status === "VALID") {
          setVerificationResult(undefined);
        }
      },
      { defer: true },
    ),
  );

  return (
    <>
      <Switch>
        <Match when={narrow(verificationResult, (v) => v.status === "NOT_VALIDATED")}>
          <InlineMessage variant="danger">
            {t(($) => $.INTEGRATIONS_VERIFICATION_RESULT_UNABLE_TO_VERIFY)}
          </InlineMessage>
        </Match>
        <Match when={narrow(verificationResult, (v) => v.status === "INVALID")}>
          {(verificationResult) => (
            <InlineMessage variant="danger">
              <div class="flex flex-col gap-2">
                <Show when={verificationResult().globalError}>
                  <p>
                    {t(($) => $.INTEGRATIONS_VERIFICATION_RESULT_FAILED, { message: verificationResult().globalError })}
                  </p>
                </Show>
                <Show when={condition(verificationResult().fieldErrors, (v) => !isEmpty(v))}>
                  {(fieldErrors) => (
                    <>
                      {t(($) => $.INTEGRATIONS_VERIFICATION_RESULT_FIELDS_WITH_ERRORS)}
                      <ul class="ml-6 list-outside list-disc">
                        <For each={Object.entries(fieldErrors())}>
                          {([key, value]) => {
                            const propertyKey = get(props.integrationType.propertyKeys, key);

                            return (
                              <li>
                                {isEmpty(propertyKey) ? key : t(propertyKey as Parameters<typeof t>[0])}:{" "}
                                {value as string}
                              </li>
                            );
                          }}
                        </For>
                      </ul>
                    </>
                  )}
                </Show>
              </div>
            </InlineMessage>
          )}
        </Match>
      </Switch>
      <div class="flex justify-end">
        <Show when={verificationResult()?.status === "VALID"}>
          <TextAndIconButton
            text={t(($) => $.INTEGRATIONS_VERIFICATION_BUTTON_LABEL_COMPLETED)}
            icon="check"
            disabled={true}
          />
        </Show>
        <Show when={verificationResult()?.status !== "VALID"}>
          <TextButton
            text={
              verifying()
                ? t(($) => $.INTEGRATIONS_VERIFICATION_BUTTON_LABEL_IN_PROGRESS)
                : t(($) => $.INTEGRATIONS_VERIFICATION_BUTTON_LABEL)
            }
            onClick={verify}
            loading={verifying()}
            disabled={props.hasErrors}
          />
        </Show>
      </div>
    </>
  );
};
